import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { QuantityStepper } from "../../../components/mobile/QuantityStepper";
import { FlowPhotoSlot } from "../../../components/mobile/FlowPhotoSlot";
import { ArticlePicker } from "../../../components/mobile/ArticlePicker";
import { ArticlePhotoPreview } from "../../../components/mobile/ArticlePhotoPreview";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Trash2 } from "lucide-react";

// #209: Menge startet leer, damit auf dem Handy direkt "24" getippt werden kann.
const emptyPosition = () => ({ article_id: "", compartment_id: "", quantity: null, serial_numbers: [] });

export default function EingangFlow() {
    const prefill = useScanPrefill();
    useEffect(() => {
        if (prefill?.compartmentId) {
            setPositions(current => current.map((position, index) => index === 0 ? { ...position, compartment_id: prefill.compartmentId } : position));
        }
    }, [prefill]);
    const flow = useFlowOperationId();
    const [step, setStep] = useState(1);
    const [supplier, setSupplier] = useState("");
    const [reference, setReference] = useState("");
    const [lsn, setLsn] = useState("");
    const [comment, setComment] = useState("");
    const [photoId, setPhotoId] = useState(null);
    const [articles, setArticles] = useState([]);
    const [compartments, setCompartments] = useState([]);
    const [positions, setPositions] = useState(() => [emptyPosition()]);
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [scanPositionIndex, setScanPositionIndex] = useState(0);

    useEffect(() => {
        Promise.all([rpc.get("/master/articles"), rpc.get("/master/compartments")]).then(
            ([a, c]) => {
                if (a.ok) setArticles(a.data);
                if (c.ok) setCompartments(c.data);
            }
        );
    }, []);

    const articleMap = Object.fromEntries(articles.map((a) => [a.id, a]));
    const addPosition = () => {
        setValidationError(null);
        setPositions((p) => [...p, emptyPosition()]);
    };
    const removePosition = (i) => {
        setValidationError(null);
        setPositions((p) => p.filter((_, x) => x !== i));
        setScanPositionIndex((current) => Math.max(0, Math.min(current, positions.length - 2)));
    };
    const updatePosition = (i, patch) => {
        setValidationError(null);
        setPositions((p) => p.map((row, x) => (x === i ? { ...row, ...patch } : row)));
    };

    const validatePositions = () => {
        if (!positions.length) {
            setValidationError({ index: null, message: "Mindestens eine Position anlegen." });
            return false;
        }
        for (let index = 0; index < positions.length; index += 1) {
            const position = positions[index];
            const number = index + 1;
            if (!position.article_id) {
                setValidationError({ index, message: `Position ${number}: Artikel auswählen.` });
                return false;
            }
            if (!position.compartment_id) {
                setValidationError({ index, message: `Position ${number}: Ziel-Fach auswählen.` });
                return false;
            }
            if (position.quantity == null) {
                setValidationError({ index, message: `Position ${number}: Menge eingeben.` });
                return false;
            }
            if (position.quantity <= 0) {
                setValidationError({ index, message: `Position ${number}: Menge muss größer als 0 sein.` });
                return false;
            }
            const article = articleMap[position.article_id];
            if (article?.type === "single") {
                const missingSerial = position.serial_numbers.findIndex((serial) => !serial.trim());
                if (position.serial_numbers.length !== position.quantity || missingSerial >= 0) {
                    const serialNumber = missingSerial >= 0 ? missingSerial + 1 : position.serial_numbers.length + 1;
                    setValidationError({ index, message: `Position ${number}: Seriennummer ${serialNumber} eingeben.` });
                    return false;
                }
            }
        }
        setValidationError(null);
        return true;
    };

    const advance = () => {
        if (step === 2 && !validatePositions()) return;
        setStep((current) => current + 1);
    };

    const goBack = () => {
        setValidationError(null);
        setStep((current) => current - 1);
    };

    const submit = async () => {
        if (!validatePositions()) {
            setStep(2);
            return;
        }
        setSubmitting(true);
        const res = await rpc.post("/receipts/with-positions", {
            client_operation_id: flow.clientOperationId,
            supplier: supplier || undefined,
            reference: reference || undefined,
            delivery_note_number: lsn || undefined,
            comment: comment || undefined,
            positions: positions.map((p) => ({
                article_id: p.article_id,
                compartment_id: p.compartment_id,
                quantity: p.quantity,
                serial_numbers: p.serial_numbers,
            })),
        });
        setSubmitting(false);
        if (!res.ok) return toast.error(res.error);
        const receiptId = res.data?.id;
        if (photoId && receiptId) {
            await rpc.patch(`/photos/${photoId}`, {
                entity_type: "receipt",
                entity_id: receiptId,
            });
        }
        toast.success(`Wareneingang gebucht (${positions.length} Pos.).`);
        flow.rotate();
        setStep(1);
        setSupplier("");
        setReference("");
        setLsn("");
        setComment("");
        setPhotoId(null);
        setPositions([emptyPosition()]);
        setScanPositionIndex(0);
    };

    return (
        <FlowShell
            testId="flow-eingang"
            title="Wareneingang"
            kind="Step-Wizard"
            stepIndicator={`${step} / 3`}
            onBack={step > 1 ? goBack : undefined}
            backLabel={step > 1 ? "Vorheriger Schritt" : "Flow verlassen"}
            onSubmit={step === 3 ? submit : advance}
            submitLabel={step === 3 ? "Buchen" : "Weiter"}
            submitDisabled={submitting}
            submitting={submitting}
        >
            {step === 1 ? (
                <>
                    <p className="text-sm text-muted-foreground" data-testid="eingang-optional-hint">
                        Kopfdaten und Lieferschein-Foto sind optional. Du kannst direkt mit „Weiter“ zu den Positionen gehen.
                    </p>
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                            Lieferant
                        </Label>
                        <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} className="h-12" aria-label="Lieferant" data-testid="eingang-supplier" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Referenz</Label>
                        <Input value={reference} onChange={(e) => setReference(e.target.value)} className="h-12" aria-label="Referenz" data-testid="eingang-reference" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                            Lieferschein-Nr.
                        </Label>
                        <Input value={lsn} onChange={(e) => setLsn(e.target.value)} className="h-12" aria-label="Lieferschein-Nr." data-testid="eingang-lsn" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kommentar</Label>
                        <Input value={comment} onChange={(e) => setComment(e.target.value)} className="h-12" aria-label="Kommentar" data-testid="eingang-comment" />
                    </div>
                    <FlowPhotoSlot
                        sessionKey={flow.clientOperationId}
                        label="Lieferschein-Foto"
                        entityType="receipt"
                        purpose="delivery_note"
                        value={photoId}
                        onChange={setPhotoId}
                        helpText="Wird automatisch mit dem Wareneingang verknüpft."
                        testId="eingang-photo-lsn"
                    />
                </>
            ) : null}

            {step === 2 ? (
                <>
                    <ScanBox
                        testId="eingang-scan"
                        placeholder={`Artikel oder Ziel-Fach für Position ${scanPositionIndex + 1} scannen`}
                        onResolved={(raw, resolved) => {
                            if (resolved?.kind === "article") {
                                const article = articleMap[resolved.id];
                                updatePosition(scanPositionIndex, {
                                    article_id: resolved.id,
                                    serial_numbers: article?.type === "single"
                                        ? Array.from({ length: positions[scanPositionIndex]?.quantity || 0 }, () => "")
                                        : [],
                                });
                                toast.success(`Artikel für Position ${scanPositionIndex + 1} übernommen.`);
                            } else if (resolved?.kind === "compartment") {
                                updatePosition(scanPositionIndex, { compartment_id: resolved.id });
                                toast.success(`Ziel-Fach für Position ${scanPositionIndex + 1} übernommen.`);
                            } else {
                                toast.warning(`Code ${raw} ist kein Artikel oder Fach.`);
                            }
                        }}
                    />
                    {validationError ? (
                        <div
                            role="alert"
                            className="rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm text-foreground"
                            data-testid="eingang-validation-error"
                        >
                            {validationError.message}
                        </div>
                    ) : null}
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">
                            Positionen ({positions.length})
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={addPosition}
                            className="h-9"
                            data-testid="eingang-pos-add"
                        >
                            + Position
                        </Button>
                    </div>
                    {positions.map((p, i) => {
                        const a = articleMap[p.article_id];
                        const isSingle = a?.type === "single";
                        return (
                            <div
                                key={i}
                                className={`rounded-lg border bg-card/40 p-3 ${validationError?.index === i ? "border-destructive" : "border-border"}`}
                                data-testid={`eingang-pos-${i}`}
                            >
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-xs font-medium text-primary">
                                        Position {i + 1}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setScanPositionIndex(i)}
                                            className={`rounded px-2 py-1 text-xs ${scanPositionIndex === i ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                                            data-testid={`eingang-pos-${i}-scan-target`}
                                        >
                                            {scanPositionIndex === i ? "Scan-Ziel" : "Hier scannen"}
                                        </button>
                                        {positions.length > 1 ? (
                                            <button
                                                type="button"
                                                onClick={() => removePosition(i)}
                                                aria-label={`Position ${i + 1} entfernen`}
                                                className="inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                data-testid={`eingang-pos-${i}-remove`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                <ArticlePicker
                                    value={p.article_id}
                                    onChange={(v) =>
                                        updatePosition(i, {
                                            article_id: v,
                                            serial_numbers:
                                                articleMap[v]?.type === "single"
                                                    ? Array.from({ length: p.quantity || 0 }, () => "")
                                                    : [],
                                        })
                                    }
                                    articles={articles}
                                    placeholder="Artikel"
                                    label={`Artikel für Position ${i + 1}`}
                                    testId={`eingang-pos-${i}-article`}
                                />
                                {a ? (
                                    <div className="mb-2">
                                        <ArticlePhotoPreview
                                            articleId={a.id}
                                            articleName={a.name}
                                            testId={`eingang-pos-${i}-article-photo`}
                                        />
                                    </div>
                                ) : null}
                                <Select value={p.compartment_id} onValueChange={(v) => updatePosition(i, { compartment_id: v })}>
                                    <SelectTrigger
                                        className="mb-2 h-11"
                                        aria-label={`Ziel-Fach für Position ${i + 1}`}
                                        data-testid={`eingang-pos-${i}-comp`}
                                    >
                                        <SelectValue placeholder="Ziel-Fach" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {compartments.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.name} ({c.code}){c.is_staging ? " [Staging]" : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Label className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Menge</Label>
                                <QuantityStepper
                                    value={p.quantity}
                                    onChange={(q) => updatePosition(i, { quantity: q, serial_numbers: isSingle ? Array.from({ length: q ?? 0 }, (_, x) => p.serial_numbers[x] || "") : [] })}
                                    min={1}
                                    placeholder="Menge eingeben"
                                    ariaLabel={`Menge für Position ${i + 1}`}
                                    testId={`eingang-pos-${i}-qty`}
                                />
                                {isSingle ? (
                                    <div className="mt-2 flex flex-col gap-1">
                                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Seriennummern
                                        </Label>
                                        {p.serial_numbers.map((sn, idx) => (
                                            <Input
                                                key={idx}
                                                aria-label={`Seriennummer ${idx + 1}`}
                                                value={sn}
                                                onChange={(e) => {
                                                    const next = [...p.serial_numbers];
                                                    next[idx] = e.target.value;
                                                    updatePosition(i, { serial_numbers: next });
                                                }}
                                                placeholder={`SN #${idx + 1}`}
                                                className="h-10"
                                                data-testid={`eingang-pos-${i}-sn-${idx}`}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </>
            ) : null}

            {step === 3 ? (
                <>
                    <div className="rounded-lg border border-border bg-card/40 p-3 text-xs text-muted-foreground">
                        <div className="mb-1 font-medium text-foreground">Review</div>
                        <div>Lieferant: {supplier || "—"}</div>
                        <div>Positionen: {positions.length}</div>
                        <div>Gesamt-Menge: {positions.reduce((s, p) => s + (p.quantity || 0), 0)}</div>
                    </div>
                    <ul className="flex flex-col gap-1 text-xs">
                        {positions.map((p, i) => {
                            const a = articleMap[p.article_id];
                            const c = compartments.find((x) => x.id === p.compartment_id);
                            return (
                                <li key={i} className="rounded border border-border/60 bg-card/40 px-3 py-2">
                                    {a?.name || "?"} × {p.quantity} → {c?.name || "?"}
                                </li>
                            );
                        })}
                    </ul>
                </>
            ) : null}
        </FlowShell>
    );
}
